import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { BehaviorSubject, catchError, Observable, tap, throwError } from 'rxjs';

import { RcEnvironment } from '../../env.config';
import { LoggerService } from '../../services/logging-service/logger.service';

import { EnvironmentConfiguration } from './environment-configuration.interface';

const CONFIG_FILE_PATH = './config/';

/**
 * A 200 with unusable content is not covered by `catchError`, and this file is the single source of
 * truth for the API hosts on cloud: without `API_URL` the ApiInterceptor silently leaves every
 * request on the CloudFront origin (403 from S3) instead of failing visibly. See TARE-18657.
 */
const isUsableConfig = (config: EnvironmentConfiguration | null): boolean =>
  !!config?.API_URL && !!config.API_WR_URL && typeof config.LOG_LVL === 'number';

@Injectable({
  providedIn: 'root',
})
export class EnvironmentConfigService {
  private readonly configSubject = new BehaviorSubject<EnvironmentConfiguration | null>(null);
  private readonly injector = inject(Injector);
  // Use HttpBackend (not HttpClient) so the config load bypasses the global
  // ApiInterceptor/AuthInterceptor — otherwise the ApiInterceptor rewrites the
  // relative './config/...' URL onto the backend host (port-override) and the
  // request fails with CORS/404. See TARE-18657.
  private readonly http = new HttpClient(inject(HttpBackend));
  private resolved: RcEnvironment | null = null;

  public loadConfig(env: RcEnvironment): Observable<EnvironmentConfiguration> {
    const configFile = `${CONFIG_FILE_PATH}config.${env}.json`;
    return this.http.get<EnvironmentConfiguration>(configFile).pipe(
      tap((config: EnvironmentConfiguration) => {
        if (!isUsableConfig(config)) {
          throw new Error(`Malformed environment config (missing API_URL/API_WR_URL/LOG_LVL): ${configFile}`);
        }
        this.resolved = env;
        this.configSubject.next(config);
      }),
      catchError(error => {
        this.injector.get(LoggerService).error(`Failed to load environment config for environment ${env}:`, error);
        return throwError(() => error);
      }),
    );
  }

  /**
   * ℹ️ Should only be read once (during bootstrapping to load into store).
   */
  public getConfig(): Observable<EnvironmentConfiguration | null> {
    return this.configSubject.asObservable();
  }

  public snapshot(): EnvironmentConfiguration | null {
    return this.configSubject.value;
  }

  public get resolvedEnv(): RcEnvironment | null {
    return this.resolved;
  }
}
